import React, { useEffect, useRef, useState } from 'react';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import Modal from '$/components/global/modal';
import InputBlock from '$/components/global/inputBlock';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import BixButton from '$/components/global/bixButton';
import { updateCloDealBixbyIdMapping } from '$/store/cloDealBixybyIdMapping/actions';
import { CloDealService } from '$/services';

export interface IEditMappingProps {
  isLoading: boolean;
  currentMapping: Bixby.Models.CloDealBixbyIdMapping | null;
  isOpen: boolean;
  onCloseModal: () => void;
  dispatchUpdateMapping: (id: string, bixbyId: string) => Promise<void>;
  dispatchGetCloDealHoldingForBixbyIdMappings: () => Promise<void>;
}

export interface EditMappingValues {
  instrumentId: string;
  instrumentName: string;
  bixbyId: string;
  id: string;
}

const loadCloCompanies = async (instrumentId: string): Promise<Bixby.Models.AdminCompanyForClientAdd[]> => {
  const result = await CloDealService.getAllCloDealCompanies(instrumentId);
  return result;
};

const EditMapping: React.FC<IEditMappingProps> = props => {
  const {
    currentMapping,
    isOpen,
    onCloseModal,
    isLoading,
    dispatchUpdateMapping,
    dispatchGetCloDealHoldingForBixbyIdMappings,
  } = props;
  const { instrumentId, instrumentName, id, bixbyId } = currentMapping ?? {};
  const [companies, setCompanies] = useState([] as Bixby.Models.AdminCompanyForClientAdd[]);
  const [filteredCompanies, setFilteredCompanies] = useState([] as Bixby.Models.AdminCompanyForClientAdd[]);

  const initialNewClo = {
    instrumentId: instrumentId ?? '',
    instrumentName: instrumentName ?? '',
    bixbyId: bixbyId ?? '',
    id: id ?? '',
  };

  useEffect(() => {
    if (instrumentId) {
      const rawData = loadCloCompanies(instrumentId);
      rawData.then(x => setCompanies(x));
    }
  }, [instrumentId]);

  const formikRef = useRef<FormikProps<EditMappingValues>>(null);

  const handleCloseModal = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }
    onCloseModal();
  };

  const schema = Yup.object({
    bixbyId: Yup.string().required('Bixby Id is required.'),
  });

  const fillDataListIfEmpty = (evt: any) => {
    const input = evt.target.value;

    if (!input) {
      setFilteredCompanies(companies ?? []);
    }
  };

  const getExactMatchDataList = (evt: any) => {
    const input = evt.target.value;
    const filtered: Bixby.Models.AdminCompanyForClientAdd[] | [] = (companies ?? []).filter(
      c => c.bixbyId.indexOf(input) != -1 || c.name.indexOf(input) != -1
    );

    setFilteredCompanies(filtered);
  };

  const validate = (evt: any) => {
    const theEvent = evt;
    const key = theEvent.charCode || theEvent.which;

    if (key >= 32 && (theEvent.ctrlKey === undefined || !theEvent.ctrlKey)) {
      if (theEvent.preventDefault) theEvent.preventDefault();
      else theEvent.returnValue = false;
    }
  };

  const updateMappingForm = (
    <Modal title="Edit mapping" onClose={handleCloseModal} active={isOpen}>
      <div>
        <Formik
          innerRef={formikRef}
          initialValues={initialNewClo}
          validationSchema={schema}
          enableReinitialize={true}
          onSubmit={async (values: EditMappingValues) => {
            if (id && values.bixbyId) {
              await dispatchUpdateMapping(id, values.bixbyId);
              await dispatchGetCloDealHoldingForBixbyIdMappings();
              handleCloseModal();
            }
          }}
          render={({ handleSubmit, setFieldValue }) => (
            <form className="create-company-form" onSubmit={handleSubmit}>
              <div className="profile-input-container">
                <div>
                  <InputBlock title="Instrument Id">
                    <div>{instrumentId}</div>
                  </InputBlock>
                  <InputBlock title="Instrument Name">
                    <div>{instrumentName}</div>
                  </InputBlock>
                  <InputBlock title="Existing Bixby Id">
                    <div>{bixbyId}</div>
                  </InputBlock>
                  <InputBlock title="New Bixby Id">
                    <input
                      list="companies"
                      name="bixbyIds"
                      onChange={e => setFieldValue('bixbyId', e.currentTarget.value)}
                      onInput={getExactMatchDataList}
                      onFocus={fillDataListIfEmpty}
                      onKeyDown={validate}
                      autoComplete="off"
                    />
                    <datalist id="companies">
                      {filteredCompanies &&
                        filteredCompanies.map(({ id, bixbyId, name }) => {
                          return (
                            <option key={id} label={`${bixbyId} - ${name}`} value={bixbyId}>
                              {`${bixbyId} - ${name}`}
                            </option>
                          );
                        })}
                    </datalist>
                  </InputBlock>
                </div>
              </div>
              <BixButton useSmall type="submit" disabled={isLoading} useLoading isLoading={isLoading}>
                Update Mapping
              </BixButton>
            </form>
          )}></Formik>
      </div>
    </Modal>
  );

  return <div>{updateMappingForm}</div>;
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.cloDealBixIdMappingState.isLoading,
    error: state.cloDealBixIdMappingState.error,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateMapping: (id: string, bixbyId: string) => dispatch(updateCloDealBixbyIdMapping(id, bixbyId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditMapping);
